import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIF_TYPES } from '@private/notifications';

import IdleCountdown from 'components/IdleCountdown';
import { useAppSelector } from 'hooks/storeHooks';
import { useConfirmModal } from 'modals/ConfirmModal';
import { notificationService } from 'utils/notificationService';

import { SESSION_CONFIG } from './constants';
import { useLogout } from './index';
import { getLastActivity, isIdleExpired, markActivity, resetLastActivity } from './utils';

const ACTIVITY_EVENTS = [
  'keydown',
  'mousemove',
  'pointerdown',
  'popstate',
  'touchstart',
  'wheel',
] as const;

const IDLE_LOGOUT_NOTIF_ID = 'idle-logout';

const onActivity = (event: Event) => {
  // Chrome fires zero-movement mousemove when content changes under a resting cursor
  if (
    event instanceof MouseEvent &&
    event.type === 'mousemove' &&
    !event.movementX &&
    !event.movementY
  ) {
    return;
  }

  markActivity();
};

export const useIdleLogout = (): void => {
  const { i18n, t } = useTranslation();
  const isAuthenticated = useAppSelector((state) => state.session.isAuthenticated);
  const { logout } = useLogout();
  const [showConfirmModal, closeModal] = useConfirmModal();
  const isWarningShown = useRef(false);
  const wasAuthenticated = useRef(isAuthenticated);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { capture: true, passive: true }),
    );

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, onActivity, { capture: true }),
      );
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    if (!getLastActivity()) {
      markActivity();
    }

    let intervalId: ReturnType<typeof setInterval>;

    const closeWarning = () => {
      if (isWarningShown.current) {
        isWarningShown.current = false;
        closeModal();
      }
    };

    const tick = () => {
      if (isIdleExpired()) {
        clearInterval(intervalId);
        closeWarning();
        logout().catch(() => {
          // onSettled has already logged out locally
        });
        return;
      }

      const remaining = getLastActivity() + SESSION_CONFIG.IDLE_TIMEOUT - Date.now();

      if (remaining > SESSION_CONFIG.IDLE_WARNING) {
        closeWarning();
        return;
      }

      if (isWarningShown.current) {
        return;
      }

      isWarningShown.current = true;
      showConfirmModal({
        confirmText: i18n.t('session.idle.stayLoggedIn'),
        message: <IdleCountdown />,
        onConfirm: () => {
          isWarningShown.current = false;
          markActivity();
        },
        title: i18n.t('session.idle.warningTitle'),
      });
    };

    intervalId = setInterval(tick, SESSION_CONFIG.IDLE_TICK);

    return () => {
      clearInterval(intervalId);
      closeWarning();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      if (!wasAuthenticated.current) {
        resetLastActivity();
      }
      notificationService.remove(IDLE_LOGOUT_NOTIF_ID);
    } else if (wasAuthenticated.current && isIdleExpired()) {
      notificationService.show({
        dismissIn: false,
        message: t('session.idle.expired'),
        title: t('notifications.warningTitle'),
        toastId: IDLE_LOGOUT_NOTIF_ID,
        type: NOTIF_TYPES.WARNING,
      });
    }

    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);
};
